import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import { DocumentComponent } from 'app/components/reusable/document/document.component';
import { SharedService } from '@shared/services/shared.service';
import { HemoTechLicenseService } from 'app/services/license/hemo-tech/hemo-tech-license.service';
import { IHemoTechLicense } from 'app/models/licenses/hemo-tech/hemo-tech-license';

@Component({
  selector: 'app-certification',
  templateUrl: './certification.component.html',
  styleUrls: ['./certification.component.scss']
})

export class CertificationComponent implements OnInit {
  @ViewChild(DocumentComponent) documents!: DocumentComponent;
  @Input() license!: IHemoTechLicense;
  @Input() isLocked: boolean = false;
  @Input() daysLate: number = 0;
  @Output() formValidityChange = new EventEmitter<boolean>();

  public documentErrors: string[] = [];
  public certificationInfoForm = new FormGroup({
    education: new FormControl('', Validators.required),
    nationalCertification: new FormControl('', this.certificationValidator())
  });

  public certificationDocumentTypes: string[] = [];

  public hemoTechService = inject(HemoTechLicenseService);
  public sharedService = inject(SharedService);

  ngOnInit() {
    this.updateCertificateForm();
    if(this.license.full){
      this.certificationDocumentTypes = ['Education Document', 'Certification Document', 'Other'];
    }
    else{
      this.certificationDocumentTypes = ['Education Document', 'Training Document', 'Other'];
    }
    if(this.license.isRenewal){
      this.certificationDocumentTypes = ['Continuing Education', 'Certification Document', 'Training Document', 'Work Hours', 'Other'];
    }
    if (this.isLocked) {
      this.certificationInfoForm.disable();
    } else {
      this.certificationInfoForm.enable();
    }
  }

  updateCertificateForm(): void {
    this.certificationInfoForm.reset({
      education: this.license.education || '',
      nationalCertification: this.license.nationalCertification || ''
    });
  }

  saveCertification(): void {
    if (this.certificationInfoForm.invalid) {
      console.log('Certification form is invalid');
      return;
    }

    const formValue = this.certificationInfoForm.value;

    this.license.education = formValue.education || '';
    this.license.nationalCertification = formValue.nationalCertification || '';

    this.hemoTechService.updateLicense(this.license).subscribe({
      next: (result) => {
        if (result) {
          this.checkCertificationValidity();
        }
      },
      error: (error) => {
        console.error('Error saving certification:', error);
      }
    });
  }

  private checkCertificationValidity() {
    const isFormValid = this.certificationInfoForm.valid;

    const certificationErrors = this.validateNationalCertification();
    const hasCertificationErrors = certificationErrors.length > 0;

    const documentErrors = this.validateDocumentUploads();
    const hasDocumentErrors = documentErrors.length > 0;

    const isCompletelyValid = isFormValid && !hasCertificationErrors && !hasDocumentErrors;
    this.formValidityChange.emit(isCompletelyValid);
  }

  isFullCertification(): boolean {
    return this.license?.full ?? false;
  }

  validateNationalCertification(): string[] {
    let errors: string[] = [];
    if (!this.license.education) {
      errors.push('Education Information is incomplete');
    }
    if (!this.isFullCertification()){
      return errors;
    }
    if (!this.license.nationalCertification) {
      errors.push('National Certification is required');
    }

    return errors;
  }

  validateDocumentUploads(): string[] {
    let errors: string[] = [];

    let documentSet = this.documents.documents;
    const hasWorkHours = documentSet.some(d => d.types.some(t => t.type === 'Work Hours'));
    const hasCertificationDoc = documentSet.some(d => d.types.some(t => t.type === 'Certification Document'));
    const hasEducationDoc = documentSet.some(d => d.types.some(t => t.type === 'Education Document'));
    const hasContinuingEducationDoc = documentSet.some(d => d.types.some(t => t.type === 'Continuing Education'));
    const hasTrainingDoc = documentSet.some(d => d.types.some(t => t.type === 'Training Document'));

    if(this.license.isRenewal && this.isFullCertification()){
      if(this.daysLate > 90 && this.daysLate < 365){
        if(!hasContinuingEducationDoc){
          errors.push('Continuing Education Documentation is Required ');
        }
        if(!hasTrainingDoc){
          errors.push('Abbreviated Training Program Documentation is Required ');
        }
        if(!hasCertificationDoc){
          errors.push('National Certification Documentation is Required ');
        }
        if(!hasWorkHours){
          errors.push('Work Hours documentation is required');
        }
      }
      else{
        if(!hasWorkHours && !hasCertificationDoc && !hasContinuingEducationDoc){
          errors.push('For renewal you must upload your work hours and continuing education, or your national certification');
        }
        else if(!hasWorkHours && hasContinuingEducationDoc){
          errors.push('Work Hours documentation is required');
        }
        else if(hasWorkHours && !hasContinuingEducationDoc){
          errors.push('Continuing Education documentation is required');
        }
      }
    }
    else{
      if(!hasEducationDoc && !this.license.isRenewal){
        errors.push('Education Documentation is Required ');
      }
      if(!this.isFullCertification() && !hasTrainingDoc && !this.license.isRenewal){
        errors.push('Training Documentation is Required ');
      }
      if(this.isFullCertification() && !hasCertificationDoc){
        errors.push('Certification Documentation is Required ');
      }
    }

    this.documentErrors = [...errors];
    return errors;
  }

  certificationValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const nationalCertification = control.value;
      if(this.isFullCertification()){
        return nationalCertification ? null : { nationalCertRequired: true };
      }
      return null;
    }
  }

}
